/**
 * Pure viewport navigation math for the time axis, in epoch milliseconds.
 *
 * Split out from `activeTimeRangeHelpers` (which owns the dayjs/TimeRange
 * plumbing) for the same reason as `timeRangeClamp`: the interesting part is
 * arithmetic with edge cases, and arithmetic is worth testing on its own.
 *
 * These helpers deliberately do *not* enforce span floors/ceilings -- that is
 * `clampTimeRange`'s job, and every caller must run the result through it before
 * committing. What they do own is the `to <= now` rule, because pan and zoom
 * each have their own coherent answer for it (see below), and the generic clamp
 * only knows how to shrink.
 */

export interface TimeRangeMs {
  /** Epoch milliseconds. */
  from: number
  /** Epoch milliseconds. */
  to: number
}

export type PanDirection = 'forward' | 'backward'
export type ZoomDirection = 'in' | 'out'

/**
 * Fraction of the visible span that one pan step moves.
 *
 * Justification: a full-window step (the previous behavior) leaves nothing on
 * screen from before the step -- every pixel is new, so there is no visual
 * anchor and the user cannot tell how the two views relate. Stepping 75% keeps
 * the trailing quarter of the old window on screen as that anchor, while still
 * being a decisive move (four steps still traverse three full windows).
 */
export const PAN_STEP_FRACTION = 0.75

/** Fraction of the previous window still visible after a pan step. */
export const PAN_OVERLAP_FRACTION = 1 - PAN_STEP_FRACTION

/**
 * Span multiplier for one zoom step. Zoom in divides, zoom out multiplies.
 */
export const ZOOM_STEP_FACTOR = 2

/**
 * Translate the window along the time axis without changing its span.
 *
 * Pan is a *translation*: the span is an invariant of the gesture, so no edge
 * case is allowed to change it. The only bound is that the window may not show
 * the future, which is expressed as a cap on the translation distance rather
 * than a cut to `to`:
 *
 *   - Panning forward near `now` moves as far as it can and lands with
 *     `to === now`. It is a partial step, not a refusal and not a shrink.
 *   - Panning forward when `to` is already at (or past) `now` cannot move
 *     forward; the window is pinned to end at `now`. `canNavigateForward`
 *     disables the control in this state, so it should rarely be reached.
 *   - Panning backward is unbounded. There is no known start-of-data timestamp
 *     at this layer, and inventing one would be a guess. Panning past the data
 *     shows an empty chart, which is the truth about that interval; silently
 *     refusing to move would not be.
 */
export function panTimeRange(
  range: TimeRangeMs,
  direction: PanDirection,
  now: number
): TimeRangeMs {
  const span = range.to - range.from
  const requestedDelta =
    direction === 'forward'
      ? span * PAN_STEP_FRACTION
      : -span * PAN_STEP_FRACTION

  // `now - to` is the largest forward translation that keeps the window out of
  // the future. It is negative when the window already ends past `now`, in
  // which case even a backward pan is pulled back far enough to end at `now`.
  const maxDelta = now - range.to
  const delta = Math.min(requestedDelta, maxDelta)

  return { from: range.from + delta, to: range.to + delta }
}

/**
 * Scale the window about its center.
 *
 * Zoom is a *scale about the midpoint*: whatever was in the middle of the
 * screen stays in the middle, and both edges move by the same amount. Anchoring
 * on an edge (the previous behavior) means half the gesture is an unrequested
 * pan.
 *
 * Zoom out near `now` cannot grow to the right, so it grows the extra span to
 * the left and lands with `to === now`. This is the one place the window is
 * knowingly not centered on where it started -- the alternative is to swallow
 * the overflow and hand back less than a 2x zoom-out, which would make the
 * control silently lie about what it did. Delivering the full requested span in
 * the only direction available is the honest reading of "show me twice as
 * much".
 *
 * The returned span is exactly `ZOOM_STEP_FACTOR` times (or divided by) the
 * input span. Callers must still run this through `clampTimeRange`, which owns
 * the span floor and ceiling; at the floor the clamp re-expands about the same
 * midpoint, so a zoom-in that has bottomed out is a stable no-op rather than a
 * drift.
 */
export function zoomTimeRange(
  range: TimeRangeMs,
  direction: ZoomDirection,
  now: number
): TimeRangeMs {
  const span = range.to - range.from
  const center = (range.from + range.to) / 2
  const nextSpan =
    direction === 'in' ? span / ZOOM_STEP_FACTOR : span * ZOOM_STEP_FACTOR

  const from = center - nextSpan / 2
  const to = center + nextSpan / 2

  // Slide (do not truncate) back into the past, preserving the zoom factor.
  const overflow = Math.max(0, to - now)

  return { from: from - overflow, to: to - overflow }
}
